/**
 * /me/timesheet — the employee's own timesheet periods + overtime status
 * (ADR-0014 Phase 4 PR-2). Off PR-0's self-scoped /v1/me/timesheet-periods +
 * /v1/me/overtime (token-derived, no employeeId param). Mirrors myLeave tables.
 */
import { createRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { CurrencyCode } from '@kora/types';
import { meLayoutRoute } from './meLayout.js';
import { listMyTimesheetPeriods, type TimesheetPeriod } from '../api/attendance.js';
import { listMyOvertime } from '../api/attendance.js';
import type { OvertimeItem } from '../api/overtime.js';
import { formatDate, formatMoney, formatNumber } from '../lib/format.js';

export const myTimesheetRoute = createRoute({
  getParentRoute: () => meLayoutRoute,
  path: '/me/timesheet',
  component: MyTimesheetPage,
});

function MyTimesheetPage(): JSX.Element {
  const periodsQuery = useQuery({ queryKey: ['me', 'timesheet-periods'], queryFn: listMyTimesheetPeriods });
  const overtimeQuery = useQuery({ queryKey: ['me', 'overtime'], queryFn: listMyOvertime });

  return (
    <section className="p-8 space-y-8 max-w-3xl">
      <header className="space-y-2">
        <h1 className="font-serif text-3xl">My timesheet</h1>
        <p className="text-sm text-gray-400">Your aggregated hours per pay period, and the status of any overtime.</p>
      </header>

      <PeriodsPanel query={periodsQuery} />
      <OvertimePanel query={overtimeQuery} />
    </section>
  );
}

function PeriodsPanel({ query }: { query: ReturnType<typeof useQuery<{ periods: TimesheetPeriod[] }>> }): JSX.Element {
  return (
    <div className="space-y-2">
      <h2 className="text-sm uppercase tracking-wide text-gray-400">Hours by period</h2>
      {query.isLoading ? (
        <Placeholder>Loading…</Placeholder>
      ) : query.isError ? (
        <Placeholder error>Could not load your timesheet.</Placeholder>
      ) : (query.data?.periods.length ?? 0) === 0 ? (
        <Placeholder>No timesheet periods yet.</Placeholder>
      ) : (
        <div className="rounded border border-gray-900 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-950 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="text-left px-4 py-2">Period</th>
                <th className="text-right px-4 py-2">Regular hrs</th>
                <th className="text-right px-4 py-2">Overtime hrs</th>
                <th className="text-right px-4 py-2">Absence days</th>
              </tr>
            </thead>
            <tbody>
              {query.data!.periods.map((p) => (
                <tr key={p.id} className="border-t border-gray-900">
                  <td className="px-4 py-3 font-mono text-xs text-gray-300">{formatDate(p.periodStart)} → {formatDate(p.periodEnd)}</td>
                  <td className="px-4 py-3 text-right font-mono text-gray-300">{formatNumber(p.regularHours)}</td>
                  <td className="px-4 py-3 text-right font-mono text-gray-300">{formatNumber(p.overtimeHours)}</td>
                  <td className="px-4 py-3 text-right font-mono text-gray-400">{formatNumber(p.absenceDays)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function OvertimePanel({ query }: { query: ReturnType<typeof useQuery<{ items: OvertimeItem[] }>> }): JSX.Element {
  return (
    <div className="space-y-2">
      <h2 className="text-sm uppercase tracking-wide text-gray-400">Overtime</h2>
      {query.isLoading ? (
        <Placeholder>Loading…</Placeholder>
      ) : query.isError ? (
        <Placeholder error>Could not load your overtime.</Placeholder>
      ) : (query.data?.items.length ?? 0) === 0 ? (
        <Placeholder>No overtime on record.</Placeholder>
      ) : (
        <div className="rounded border border-gray-900 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-950 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="text-left px-4 py-2">Period</th>
                <th className="text-right px-4 py-2">Hours</th>
                <th className="text-right px-4 py-2">Amount</th>
                <th className="text-left px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {query.data!.items.map((it) => (
                <tr key={it.id} className="border-t border-gray-900">
                  <td className="px-4 py-3 font-mono text-xs text-gray-300">{formatDate(it.periodStart)} → {formatDate(it.periodEnd)}</td>
                  <td className="px-4 py-3 text-right font-mono text-gray-300">{formatNumber(it.overtimeHours)}</td>
                  <td className="px-4 py-3 text-right font-mono text-gray-300">{formatMoney(it.computedAmount, it.currency as CurrencyCode)}</td>
                  <td className="px-4 py-3"><OvertimeStatusPill status={it.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Standard numbered scales (not the single-hex brand tokens). */
function OvertimeStatusPill({ status }: { status: string }): JSX.Element {
  const map: Record<string, string> = {
    computed: 'bg-gray-900 text-gray-400 border-gray-800',
    pending_manager: 'bg-yellow-950 text-yellow-400 border-yellow-900',
    pending_hr_admin: 'bg-yellow-950 text-yellow-400 border-yellow-900',
    approved: 'bg-green-950 text-green-400 border-green-900',
    rejected: 'bg-red-950 text-red-400 border-red-900',
  };
  const label: Record<string, string> = {
    pending_manager: 'awaiting manager',
    pending_hr_admin: 'awaiting HR',
    approved: 'approved',
    rejected: 'rejected',
    computed: 'computed',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded border ${map[status] ?? 'border-gray-800'}`}>
      {label[status] ?? status}
    </span>
  );
}

function Placeholder({ children, error }: { children: React.ReactNode; error?: boolean }): JSX.Element {
  return (
    <div className={`rounded border p-6 text-center text-sm ${error ? 'border-red-900 bg-red-950/40 text-red-300' : 'border-gray-900 text-gray-500'}`}>
      {children}
    </div>
  );
}
